import { Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  ActionButtonOutcomesType,
  ActionButtonWidgetActionsOutcomesNavigationConfigMap,
  BaseWidgetProperties,
  ButtonActionType,
  DummyRole,
  NavigationOptionEnum,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { NavigationService } from '../../services/navigation-service/navigation.service';
import { RoleState } from '../../state/role.state';
import { handleExternalLinkOpen } from '../utils';
import { redirectToPage } from './cta-button-actions.utils';

export const buildActionNavigationTargetUrl = (
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
  type: ActionButtonOutcomesType,
  isLoggedOutZone = false,
) => {
  const navigationConfig = widgetProperties.actionNavigationConfigurations?.[type];
  if (!navigationConfig) {
    return '';
  }

  const navigationService = injector.get(NavigationService);
  const store = injector.get(Store);

  const role = navigationConfig?.selectedRole;
  // ---- START of dummy role handling
  // Overrides config role if its dummy role to current role of user
  if (role === DummyRole.EMPTY_ROLE) {
    const currentRole = store.selectSnapshot(RoleState.getRole).role;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    navigationConfig!.selectedRole = currentRole;
  }
  // ---- END of dummy role handling
  const pageId = navigationConfig?.pageId || navigationConfig?.selectedPageId;

  const { pageUrl: url } = navigationService.buildPageNavigationUrl(
    {
      pageId: pageId || '', // using this it will generate url based on pageId and ignore pageUrl, if pageId is present in widget config
      zone: navigationConfig?.selectedZone || '',
      pageUrl: navigationConfig?.selectedPageUrl || '',
      role: navigationConfig?.selectedRole || '',
      navigationOption: navigationConfig?.selectedNavigationOption || NavigationOptionEnum.USE_AS_NAVIGATION,
      tabRef: navigationConfig?.tabRef || '',
      pageTransition: navigationConfig?.pageTransition || 'forward',
      browserBehaviour: navigationConfig?.selectedBrowserBehaviour,
      externalUrl: navigationConfig?.externalUrl,
    },
    isLoggedOutZone,
  );
  return url;
};

export function handleRedirection(
  widgetProperties: BaseWidgetProperties,
  injector: Injector,
  type: ActionButtonOutcomesType,
  replaceUrl = false,
  isLoggedOutZone = false,
  buttonActionType?: ButtonActionType, // used for fallback navigation when specific navigation config for outcome state is not present in widget properties
) {
  const router = injector.get(Router);

  const navigationConfig = widgetProperties?.actionNavigationConfigurations?.[type];

  const externalLink = navigationConfig?.externalUrl;
  const browserBehaviour = navigationConfig?.selectedBrowserBehaviour;

  // If external link is provided then handle it and open in specified browser behavior
  if (handleExternalLinkOpen(externalLink, browserBehaviour)) {
    return;
  }

  //check if navigation config exist for outcome state then use that to navigate
  if (navigationConfig) {
    const targetUrl = buildActionNavigationTargetUrl(widgetProperties, injector, type, isLoggedOutZone);
    router.navigateByUrl(targetUrl, { replaceUrl });
  } else if (buttonActionType) {
    /**
     * This is to handle the scenario when there is no specific navigation config for success or failure state but there is a general navigation config for the button action type, in that case we will use the general navigation config to navigate.
     */
    const actionButtonNavigationConfig =
      ActionButtonWidgetActionsOutcomesNavigationConfigMap.get(buttonActionType);

    if (actionButtonNavigationConfig?.[type]) {
      widgetProperties = {
        ...widgetProperties,
        actionNavigationConfigurations: {
          ...widgetProperties?.actionNavigationConfigurations,
          [type]: actionButtonNavigationConfig[type],
        },
      };

      const targetUrl = buildActionNavigationTargetUrl(widgetProperties, injector, type, isLoggedOutZone);
      router.navigateByUrl(targetUrl, { replaceUrl });
    }
  } else {
    //if no navigation config exist for failure state then use default navigation
    redirectToPage(router, widgetProperties, injector, replaceUrl);
  }
}
